/**
 * Canonical Saudi city list for every city picker (home hero, /search, requests).
 *
 * The owner's R4 decision: the city DROPDOWN shows these standard names — «مكة
 * المكرمة» not «مكة», «جدة» not «جده» — in this featured order, whatever spelling
 * the database rows carry. It is a display list only: database `City` rows are
 * never renamed or modified. hooks/useSaudiCities attaches each row's `cityId`
 * (exact search filter) by matching names through `matchSaudiCity()`.
 *
 * Adding a city: append it to its region below with the standard Arabic spelling,
 * the common English name and the variants people type or the database stores in
 * `aliases`. The tests in __tests__/config/saudiCities.test.ts keep slugs, names
 * and match keys unique.
 */
import { stripDefiniteArticle, toSearchKey } from '../utils/arabicNormalize';

/** ISO 3166-2:SA region codes (there is no SA-13). */
export type SaudiRegionCode =
  | 'SA-01'
  | 'SA-02'
  | 'SA-03'
  | 'SA-04'
  | 'SA-05'
  | 'SA-06'
  | 'SA-07'
  | 'SA-08'
  | 'SA-09'
  | 'SA-10'
  | 'SA-11'
  | 'SA-12'
  | 'SA-14';

export interface SaudiCity {
  /** Our stable id (featured order, card images, i18n keys) — NOT the database City.slug. */
  slug: string;
  /** Standard Arabic name, as displayed. */
  nameAr: string;
  /** Common English name, as displayed. */
  nameEn: string;
  region: SaudiRegionCode;
  /** Other spellings people type or the database stores. Used for matching only — never shown. */
  aliases: readonly string[];
}

/** A canonical city plus its database id when one matched (null = filter by name). */
export type ResolvedSaudiCity = SaudiCity & { cityId: string | null };

export const SAUDI_REGIONS: Readonly<Record<SaudiRegionCode, { readonly nameAr: string; readonly nameEn: string }>> = {
  'SA-01': { nameAr: 'منطقة الرياض', nameEn: 'Riyadh Region' },
  'SA-02': { nameAr: 'منطقة مكة المكرمة', nameEn: 'Makkah Region' },
  'SA-03': { nameAr: 'منطقة المدينة المنورة', nameEn: 'Madinah Region' },
  'SA-04': { nameAr: 'المنطقة الشرقية', nameEn: 'Eastern Province' },
  'SA-05': { nameAr: 'منطقة القصيم', nameEn: 'Al-Qassim Region' },
  'SA-06': { nameAr: 'منطقة حائل', nameEn: 'Hail Region' },
  'SA-07': { nameAr: 'منطقة تبوك', nameEn: 'Tabuk Region' },
  'SA-08': { nameAr: 'منطقة الحدود الشمالية', nameEn: 'Northern Borders Region' },
  'SA-09': { nameAr: 'منطقة جازان', nameEn: 'Jazan Region' },
  'SA-10': { nameAr: 'منطقة نجران', nameEn: 'Najran Region' },
  'SA-11': { nameAr: 'منطقة الباحة', nameEn: 'Al-Bahah Region' },
  'SA-12': { nameAr: 'منطقة الجوف', nameEn: 'Al-Jouf Region' },
  'SA-14': { nameAr: 'منطقة عسير', nameEn: 'Asir Region' },
};

/** The owner's order for featured cities (hero picker top, home city cards). */
export const FEATURED_CITY_SLUGS = ['makkah', 'madinah', 'riyadh', 'jeddah', 'dammam'] as const;

export type FeaturedCitySlug = (typeof FEATURED_CITY_SLUGS)[number];

export const SAUDI_CITIES: readonly SaudiCity[] = [
  // ── Featured ──────────────────────────────────────────────────────────────
  { slug: 'makkah', nameAr: 'مكة المكرمة', nameEn: 'Makkah', region: 'SA-02', aliases: ['مكة', 'Mecca', 'Makka', 'Makkah Al Mukarramah'] },
  { slug: 'madinah', nameAr: 'المدينة المنورة', nameEn: 'Madinah', region: 'SA-03', aliases: ['المدينة', 'Medina', 'Madina', 'Al Madinah', 'Madinah Al Munawwarah', 'Al Madinah Al Munawwarah'] },
  { slug: 'riyadh', nameAr: 'الرياض', nameEn: 'Riyadh', region: 'SA-01', aliases: ['Riyad'] },
  { slug: 'jeddah', nameAr: 'جدة', nameEn: 'Jeddah', region: 'SA-02', aliases: ['Jiddah', 'Jidda', 'Jedda'] },
  { slug: 'dammam', nameAr: 'الدمام', nameEn: 'Dammam', region: 'SA-04', aliases: ['Damam'] },

  // ── SA-01 Riyadh Region ───────────────────────────────────────────────────
  { slug: 'al-kharj', nameAr: 'الخرج', nameEn: 'Al Kharj', region: 'SA-01', aliases: [] },
  { slug: 'diriyah', nameAr: 'الدرعية', nameEn: 'Diriyah', region: 'SA-01', aliases: ['Diriyya'] },
  { slug: 'al-majmaah', nameAr: 'المجمعة', nameEn: 'Al Majmaah', region: 'SA-01', aliases: ['Majmaa'] },
  { slug: 'ad-dawadimi', nameAr: 'الدوادمي', nameEn: 'Ad Dawadimi', region: 'SA-01', aliases: ['Dawadmi', 'Duwadimi'] },
  { slug: 'az-zulfi', nameAr: 'الزلفي', nameEn: 'Az Zulfi', region: 'SA-01', aliases: [] },
  { slug: 'wadi-ad-dawasir', nameAr: 'وادي الدواسر', nameEn: 'Wadi Ad Dawasir', region: 'SA-01', aliases: ['Wadi Al Dawasir', 'Wadi Aldawasir'] },
  { slug: 'shaqra', nameAr: 'شقراء', nameEn: 'Shaqra', region: 'SA-01', aliases: ['شقرا', 'Shaqraa'] },
  { slug: 'afif', nameAr: 'عفيف', nameEn: 'Afif', region: 'SA-01', aliases: [] },
  { slug: 'al-quwayiyah', nameAr: 'القويعية', nameEn: 'Al Quwayiyah', region: 'SA-01', aliases: ['Al Quwaiyah', 'Al Quwayiyyah'] },
  { slug: 'al-aflaj', nameAr: 'الأفلاج', nameEn: 'Al Aflaj', region: 'SA-01', aliases: ['ليلى', 'Layla'] },
  { slug: 'al-muzahimiyah', nameAr: 'المزاحمية', nameEn: 'Al Muzahimiyah', region: 'SA-01', aliases: ['Al Muzahmiyah', 'Al Muzahmiyya'] },
  { slug: 'ad-dilam', nameAr: 'الدلم', nameEn: 'Ad Dilam', region: 'SA-01', aliases: [] },
  { slug: 'hawtat-bani-tamim', nameAr: 'حوطة بني تميم', nameEn: 'Hawtat Bani Tamim', region: 'SA-01', aliases: ['Howtat Bani Tamim', 'Hotat Bani Tamim'] },
  { slug: 'thadiq', nameAr: 'ثادق', nameEn: 'Thadiq', region: 'SA-01', aliases: ['Thadeq'] },
  { slug: 'marat', nameAr: 'مرات', nameEn: 'Marat', region: 'SA-01', aliases: [] },
  { slug: 'rumah', nameAr: 'رماح', nameEn: 'Rumah', region: 'SA-01', aliases: [] },
  { slug: 'dhurma', nameAr: 'ضرماء', nameEn: 'Dhurma', region: 'SA-01', aliases: ['ضرما', 'Duruma', 'Durma'] },

  // ── SA-02 Makkah Region ───────────────────────────────────────────────────
  { slug: 'taif', nameAr: 'الطائف', nameEn: 'Taif', region: 'SA-02', aliases: [] },
  { slug: 'rabigh', nameAr: 'رابغ', nameEn: 'Rabigh', region: 'SA-02', aliases: [] },
  { slug: 'al-qunfudhah', nameAr: 'القنفذة', nameEn: 'Al Qunfudhah', region: 'SA-02', aliases: ['Al Qunfudah', 'Qunfudah'] },
  { slug: 'al-lith', nameAr: 'الليث', nameEn: 'Al Lith', region: 'SA-02', aliases: ['Al Leith', 'Allith'] },
  { slug: 'al-jumum', nameAr: 'الجموم', nameEn: 'Al Jumum', region: 'SA-02', aliases: ['Al Jamoum'] },
  { slug: 'khulais', nameAr: 'خليص', nameEn: 'Khulais', region: 'SA-02', aliases: ['Khulays'] },
  { slug: 'turabah', nameAr: 'تربة', nameEn: 'Turabah', region: 'SA-02', aliases: ['Turbah', 'Turaba'] },

  // ── SA-03 Madinah Region ──────────────────────────────────────────────────
  { slug: 'yanbu', nameAr: 'ينبع', nameEn: 'Yanbu', region: 'SA-03', aliases: ['ينبع البحر', 'Yanbu Al Bahr', 'Yenbo'] },
  { slug: 'alula', nameAr: 'العلا', nameEn: 'AlUla', region: 'SA-03', aliases: ['Al Ula', 'Ula'] },
  { slug: 'badr', nameAr: 'بدر', nameEn: 'Badr', region: 'SA-03', aliases: [] },
  { slug: 'al-hinakiyah', nameAr: 'الحناكية', nameEn: 'Al Hinakiyah', region: 'SA-03', aliases: ['Al Henakiyah', 'Al Hanakiyah'] },

  // ── SA-04 Eastern Province ────────────────────────────────────────────────
  { slug: 'al-khobar', nameAr: 'الخبر', nameEn: 'Al Khobar', region: 'SA-04', aliases: ['Alkhobar'] },
  { slug: 'dhahran', nameAr: 'الظهران', nameEn: 'Dhahran', region: 'SA-04', aliases: [] },
  { slug: 'al-ahsa', nameAr: 'الأحساء', nameEn: 'Al Ahsa', region: 'SA-04', aliases: ['الحسا', 'الهفوف', 'المبرز', 'Al Hasa', 'Alahsa', 'Al Hofuf', 'Al Hufuf', 'Al Hafuf', 'Al Mubarraz'] },
  { slug: 'jubail', nameAr: 'الجبيل', nameEn: 'Jubail', region: 'SA-04', aliases: [] },
  { slug: 'jubail-industrial-city', nameAr: 'الجبيل الصناعية', nameEn: 'Jubail Industrial City', region: 'SA-04', aliases: ['مدينة الجبيل الصناعية', 'Jubail Industrial'] },
  { slug: 'qatif', nameAr: 'القطيف', nameEn: 'Qatif', region: 'SA-04', aliases: [] },
  { slug: 'hafar-al-batin', nameAr: 'حفر الباطن', nameEn: 'Hafar Al Batin', region: 'SA-04', aliases: ['Hafr Al Batin', 'Hafr Albatin', 'Hafar Albatin'] },
  { slug: 'khafji', nameAr: 'الخفجي', nameEn: 'Khafji', region: 'SA-04', aliases: [] },
  { slug: 'ras-tanura', nameAr: 'رأس تنورة', nameEn: 'Ras Tanura', region: 'SA-04', aliases: ['Ras Tannurah', 'Ras Tanourah'] },
  { slug: 'abqaiq', nameAr: 'بقيق', nameEn: 'Abqaiq', region: 'SA-04', aliases: ['أبقيق', 'Buqayq', 'Buqaiq'] },
  { slug: 'an-nairyah', nameAr: 'النعيرية', nameEn: 'An Nairyah', region: 'SA-04', aliases: ['An Nuayriyah'] },
  { slug: 'al-jafr', nameAr: 'الجفر', nameEn: 'Al Jafr', region: 'SA-04', aliases: [] },

  // ── SA-05 Al-Qassim Region ────────────────────────────────────────────────
  { slug: 'buraydah', nameAr: 'بريدة', nameEn: 'Buraydah', region: 'SA-05', aliases: ['Buraidah', 'Buraida', 'Burayda'] },
  { slug: 'unayzah', nameAr: 'عنيزة', nameEn: 'Unayzah', region: 'SA-05', aliases: ['Unaizah', 'Onaizah', 'Unaiza'] },
  { slug: 'ar-rass', nameAr: 'الرس', nameEn: 'Ar Rass', region: 'SA-05', aliases: [] },
  { slug: 'al-bukayriyah', nameAr: 'البكيرية', nameEn: 'Al Bukayriyah', region: 'SA-05', aliases: ['Al Bukairiyah'] },
  { slug: 'al-mithnab', nameAr: 'المذنب', nameEn: 'Al Mithnab', region: 'SA-05', aliases: ['Al Midhnab', 'Al Muthnib'] },
  { slug: 'al-badai', nameAr: 'البدائع', nameEn: 'Al Badai', region: 'SA-05', aliases: [] },
  { slug: 'riyadh-al-khabra', nameAr: 'رياض الخبراء', nameEn: 'Riyadh Al Khabra', region: 'SA-05', aliases: ['Riyad Al Khabra'] },
  { slug: 'uyun-al-jiwa', nameAr: 'عيون الجواء', nameEn: 'Uyun Al Jiwa', region: 'SA-05', aliases: ['Uyun Al Jawa', 'Oyoun Al Jawa'] },

  // ── SA-06 Hail Region ─────────────────────────────────────────────────────
  { slug: 'hail', nameAr: 'حائل', nameEn: 'Hail', region: 'SA-06', aliases: ['Hayil'] },
  { slug: 'baqaa', nameAr: 'بقعاء', nameEn: 'Baqaa', region: 'SA-06', aliases: ['Baqa'] },

  // ── SA-07 Tabuk Region ────────────────────────────────────────────────────
  { slug: 'tabuk', nameAr: 'تبوك', nameEn: 'Tabuk', region: 'SA-07', aliases: ['Tabouk'] },
  { slug: 'al-wajh', nameAr: 'الوجه', nameEn: 'Al Wajh', region: 'SA-07', aliases: ['Al Wejh'] },
  { slug: 'duba', nameAr: 'ضباء', nameEn: 'Duba', region: 'SA-07', aliases: ['ضبا', 'Dhuba'] },
  { slug: 'tayma', nameAr: 'تيماء', nameEn: 'Tayma', region: 'SA-07', aliases: ['تيما', 'Taima', 'Teima'] },
  { slug: 'umluj', nameAr: 'أملج', nameEn: 'Umluj', region: 'SA-07', aliases: ['Umm Lajj'] },

  // ── SA-08 Northern Borders Region ─────────────────────────────────────────
  { slug: 'arar', nameAr: 'عرعر', nameEn: 'Arar', region: 'SA-08', aliases: [] },
  { slug: 'rafha', nameAr: 'رفحاء', nameEn: 'Rafha', region: 'SA-08', aliases: ['رفحا', 'Rafhaa'] },
  { slug: 'turaif', nameAr: 'طريف', nameEn: 'Turaif', region: 'SA-08', aliases: ['Turayf'] },

  // ── SA-09 Jazan Region ────────────────────────────────────────────────────
  { slug: 'jazan', nameAr: 'جازان', nameEn: 'Jazan', region: 'SA-09', aliases: ['جيزان', 'Jizan', 'Gizan'] },
  { slug: 'sabya', nameAr: 'صبيا', nameEn: 'Sabya', region: 'SA-09', aliases: ['Sabia', 'Sabiya'] },
  { slug: 'abu-arish', nameAr: 'أبو عريش', nameEn: 'Abu Arish', region: 'SA-09', aliases: ['Abu Areesh'] },
  { slug: 'samtah', nameAr: 'صامطة', nameEn: 'Samtah', region: 'SA-09', aliases: ['Samta'] },

  // ── SA-10 Najran Region ───────────────────────────────────────────────────
  { slug: 'najran', nameAr: 'نجران', nameEn: 'Najran', region: 'SA-10', aliases: [] },
  { slug: 'sharurah', nameAr: 'شرورة', nameEn: 'Sharurah', region: 'SA-10', aliases: ['Sharorah', 'Sharourah'] },

  // ── SA-11 Al-Bahah Region ─────────────────────────────────────────────────
  { slug: 'al-bahah', nameAr: 'الباحة', nameEn: 'Al Bahah', region: 'SA-11', aliases: ['Al Baha'] },
  { slug: 'baljurashi', nameAr: 'بلجرشي', nameEn: 'Baljurashi', region: 'SA-11', aliases: ['Biljurashi', 'Baljurshi'] },

  // ── SA-12 Al-Jouf Region ──────────────────────────────────────────────────
  { slug: 'sakaka', nameAr: 'سكاكا', nameEn: 'Sakaka', region: 'SA-12', aliases: ['Skaka'] },
  { slug: 'al-qurayyat', nameAr: 'القريات', nameEn: 'Al Qurayyat', region: 'SA-12', aliases: ['Gurayat'] },
  { slug: 'dumat-al-jandal', nameAr: 'دومة الجندل', nameEn: 'Dumat Al Jandal', region: 'SA-12', aliases: ['Dawmat Al Jandal'] },

  // ── SA-14 Asir Region ─────────────────────────────────────────────────────
  { slug: 'abha', nameAr: 'أبها', nameEn: 'Abha', region: 'SA-14', aliases: [] },
  { slug: 'khamis-mushait', nameAr: 'خميس مشيط', nameEn: 'Khamis Mushait', region: 'SA-14', aliases: ['خميس', 'Khamis', 'Khamis Mushayt'] },
  { slug: 'bisha', nameAr: 'بيشة', nameEn: 'Bisha', region: 'SA-14', aliases: ['Bishah'] },
  { slug: 'muhayil-asir', nameAr: 'محايل عسير', nameEn: 'Muhayil Asir', region: 'SA-14', aliases: ['محايل', 'Muhayil', 'Muhail'] },
  { slug: 'ahad-rafidah', nameAr: 'أحد رفيدة', nameEn: 'Ahad Rafidah', region: 'SA-14', aliases: ['Ahad Rufaidah'] },
];

// ── Matching ────────────────────────────────────────────────────────────────

const BY_SLUG = new Map<string, SaudiCity>(SAUDI_CITIES.map((city) => [city.slug, city]));

/** Every name a city answers to: Arabic, English, our slug and its aliases. */
function cityNames(city: SaudiCity): string[] {
  return [city.nameAr, city.nameEn, city.slug, ...city.aliases];
}

/** Each name's match key, plus the key without a leading «ال» / "Al ". */
function nameKeys(name: string): string[] {
  const key = toSearchKey(name);
  if (!key) return [];
  const bare = stripDefiniteArticle(key);
  return bare && bare !== key ? [key, bare] : [key];
}

/** Match key → city. Built once; the tests assert no key points at two cities. */
const MATCH_INDEX: ReadonlyMap<string, SaudiCity> = (() => {
  const index = new Map<string, SaudiCity>();
  for (const city of SAUDI_CITIES) {
    for (const name of cityNames(city)) {
      for (const key of nameKeys(name)) {
        if (!index.has(key)) index.set(key, city);
      }
    }
  }
  return index;
})();

/** Every match key of one city (exported for the integrity tests). */
export function getCityMatchKeys(city: SaudiCity): string[] {
  return [...new Set(cityNames(city).flatMap(nameKeys))];
}

/** A canonical city by our slug. */
export function getSaudiCity(slug: string): SaudiCity | undefined {
  return BY_SLUG.get(slug);
}

/**
 * The canonical city a free-text name refers to, or null. Arabic-normalized
 * (alef/hamza, ة/ه, ى/ي, tashkeel, tatweel), Latin case- and punctuation-folded,
 * and retried without a leading «ال» / "Al ": «جده» → جدة, «مكه» → مكة المكرمة,
 * "Medina" → المدينة المنورة, "At-Taif" → الطائف. Whole-name match only, so a
 * district or "region - city" string never matches a city.
 */
export function matchSaudiCity(name: string | null | undefined): SaudiCity | null {
  const key = toSearchKey(name);
  if (!key) return null;
  return MATCH_INDEX.get(key) ?? MATCH_INDEX.get(stripDefiniteArticle(key)) ?? null;
}

/** The featured cities found in `cities`, in the owner's order. */
export function getFeaturedCities<T extends SaudiCity>(cities: readonly T[]): T[] {
  const bySlug = new Map(cities.map((city) => [city.slug, city]));
  return FEATURED_CITY_SLUGS.map((slug) => bySlug.get(slug)).filter((city): city is T => Boolean(city));
}

const collators: Partial<Record<'ar' | 'en', Intl.Collator>> = {};
function collator(lang: 'ar' | 'en'): Intl.Collator {
  return (collators[lang] ??= new Intl.Collator(lang));
}

/**
 * Display order: the featured cities first (owner's order), then the rest
 * alphabetically by their name in `lang`.
 */
export function orderSaudiCities<T extends SaudiCity>(cities: readonly T[], lang: 'ar' | 'en' = 'ar'): T[] {
  const featured = getFeaturedCities(cities);
  const featuredSlugs = new Set(featured.map((city) => city.slug));
  const rest = cities
    .filter((city) => !featuredSlugs.has(city.slug))
    .sort((a, b) => collator(lang).compare(lang === 'en' ? a.nameEn : a.nameAr, lang === 'en' ? b.nameEn : b.nameAr));
  return [...featured, ...rest];
}

const searchKeyCache = new WeakMap<SaudiCity, string[]>();
function searchKeysOf(city: SaudiCity): string[] {
  let keys = searchKeyCache.get(city);
  if (!keys) {
    // Our slug is an internal id, not something people type — leave it out here.
    keys = [...new Set([city.nameAr, city.nameEn, ...city.aliases].flatMap(nameKeys))];
    searchKeyCache.set(city, keys);
  }
  return keys;
}

/**
 * Type-ahead search over Arabic names, English names and aliases with the same
 * normalization as `matchSaudiCity`. Ranking: exact name, then name prefix, then a
 * word prefix («مشيط» → خميس مشيط), then any substring; ties keep the input order
 * (so featured cities stay first). An empty query returns `cities` unchanged.
 */
export function searchSaudiCities<T extends SaudiCity>(query: string, cities: readonly T[]): T[] {
  const q = toSearchKey(query);
  if (!q) return [...cities];
  const bare = stripDefiniteArticle(q);
  const needles = bare && bare !== q ? [q, bare] : [q];

  const rank = (city: SaudiCity): number => {
    let best = 4;
    for (const key of searchKeysOf(city)) {
      for (const needle of needles) {
        if (key === needle) return 0;
        if (key.startsWith(needle)) best = Math.min(best, 1);
        else if (key.split(' ').some((word) => word.startsWith(needle))) best = Math.min(best, 2);
        else if (key.includes(needle)) best = Math.min(best, 3);
      }
    }
    return best;
  };

  return cities
    .map((city, index) => ({ city, index, score: rank(city) }))
    .filter((entry) => entry.score < 4)
    .sort((a, b) => a.score - b.score || a.index - b.index)
    .map((entry) => entry.city);
}

// ── Database rows → canonical cities ───────────────────────────────────────

/** The fields of a `GET /locations/cities` row this module reads. */
export interface CityRowLike {
  id: string;
  nameEn?: string | null;
  nameAr?: string | null;
  slug?: string | null;
  propertyCount?: number | null;
  country?: { code?: string | null } | null;
}

/** Placeholder rows minted by old imports or quarantined by the resolver. */
const JUNK_SLUG_RE = /^(?:prop-|pending-city-)/i;

/**
 * Attaches database ids to the canonical list. Each SA row is matched by its
 * Arabic name, English name and slug; when several rows map to one city (e.g.
 * «جدة · 1493» and «Jeddah · 8») the one with more listings wins, then the one
 * whose Arabic name is the canonical spelling. Rows that are placeholders
 * (`prop-*`, `pending-city-*`), outside Saudi Arabia, unmatched or ambiguous are
 * ignored — they never reach a dropdown. Read-only: rows are never modified.
 *
 * @returns every canonical city, in display order, with `cityId` or null.
 */
export function resolveCityRows(rows: readonly CityRowLike[]): ResolvedSaudiCity[] {
  const best = new Map<string, { id: string; count: number; canonical: boolean }>();

  for (const row of rows) {
    if (!row || typeof row.id !== 'string' || !row.id) continue;
    if (row.slug && JUNK_SLUG_RE.test(row.slug)) continue;
    const countryCode = row.country?.code;
    if (countryCode && countryCode.toUpperCase() !== 'SA') continue;

    const matches = new Set<SaudiCity>();
    for (const name of [row.nameAr, row.nameEn, row.slug]) {
      const city = matchSaudiCity(name);
      if (city) matches.add(city);
    }
    if (matches.size !== 1) continue; // unmatched, or names pointing at different cities
    const [city] = matches;

    const count = typeof row.propertyCount === 'number' && Number.isFinite(row.propertyCount) ? row.propertyCount : 0;
    // Exact spelling, not the normalized key («جده» and «جدة» share a key).
    const canonical = (row.nameAr ?? '').replace(/\s+/g, ' ').trim() === city.nameAr;
    const current = best.get(city.slug);
    if (!current || count > current.count || (count === current.count && canonical && !current.canonical)) {
      best.set(city.slug, { id: row.id, count, canonical });
    }
  }

  return orderSaudiCities(SAUDI_CITIES).map((city) => ({ ...city, cityId: best.get(city.slug)?.id ?? null }));
}
